import { Client } from 'appwrite';

class RealtimeManager {
  private client: Client;
  private subscriptions: Map<string, () => void> = new Map();
  private connectionPromise: Promise<void> | null = null;
  private isConnected = false;

  constructor() {
    this.client = new Client()
      .setEndpoint(import.meta.env.VITE_APPWRITE_ENDPOINT!)
      .setProject(import.meta.env.VITE_APPWRITE_PROJECT_ID!);
  }

  private async ensureConnection(): Promise<void> {
    if (this.isConnected) return;
    
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, _reject) => {
      // Ensure we have a valid session
      const session = sessionStorage.getItem('appwrite-session');
      if (session) {
        this.client.setSession(session);
      }

      // Test connection
      setTimeout(() => {
        this.isConnected = true;
        resolve();
      }, 100);
    });

    return this.connectionPromise;
  }

  async subscribe(
    channels: string[], 
    callback: (response: any) => void
  ): Promise<string> {
    await this.ensureConnection();
    
    const subscriptionId = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      // console.log('Subscribing to channels:', channels);
      
      const unsubscribe = this.client.subscribe(channels, (response) => {
        // console.log('Realtime event received:', response);
        callback(response);
      });
      
      this.subscriptions.set(subscriptionId, unsubscribe);
      // console.log('Successfully subscribed with ID:', subscriptionId);
      
      return subscriptionId;
    } catch (error) {
      // console.error('Failed to subscribe to realtime:', error);
      throw error;
    }
  }

  unsubscribe(subscriptionId: string): void {
    const unsubscribeFn = this.subscriptions.get(subscriptionId);
    if (unsubscribeFn) {
      try {
        unsubscribeFn();
        this.subscriptions.delete(subscriptionId);
        // console.log('Successfully unsubscribed:', subscriptionId);
      } catch (error) {
        // console.error('Error unsubscribing:', error);
      }
    }
  }

  unsubscribeAll(): void {
    this.subscriptions.forEach((unsubscribeFn, _id) => {
      try {
        unsubscribeFn();
      } catch (error) {
        // console.error('Error unsubscribing:', id, error);
      }
    });
    this.subscriptions.clear();
    this.isConnected = false;
    this.connectionPromise = null;
  }

  getActiveSubscriptions(): string[] {
    return Array.from(this.subscriptions.keys());
  }
}

export const realtimeManager = new RealtimeManager();